import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';

import omit from 'lodash.omit';
import {dataTypeString} from 'src/components/data-types/utils/dataTypeString';
import {NO_DATA_AVAILABLE} from 'src/constants';
import {AttributeReviewFeedback} from 'src/modules/ai';
import {
    DescriptionAutosuggest,
} from 'src/modules/event-create/attributes-block/attribute-form/suggest/description-autosuggest';
import {isAttributeEmpty} from 'src/modules/event-create/attributes-block/attribute-form/utils/isAttributeEmpty';
import {AttributeUsage} from 'src/openapi/models/AttributeUsage';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {FieldType} from 'src/openapi/models/FieldType';
import {ItemsType} from 'src/openapi/models/ItemsType';
import {possibleValuesToString} from 'src/utils/possibleValuesToString';
import {sanitizeSnakeCase} from 'src/utils/sanitizeSnakeCase';

import {CheckBox} from '@pb/kit-component-checkbox';
import {Group, Item} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Input} from '@pb/kit-component-input-v2';
import {Tooltip} from '@pb/kit-component-popups';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {formatDate} from '@pb/kit-formatters';

import {
    EventCreationMode,
    FIELDS_PATTERN,
    FIELDS_PATTERN_VALIDATION_MESSAGE,
    REQUIRED_ATTRIBUTES,
} from '../../constants';
import {CreationAttribute, FieldName} from '../../typedefs';
import {attributeDescriptionValidation} from '../../validations/attribute-description-validation';
import {MAX_LENGTH_ATTRIBUTE_NAME, MIN_LENGTH_ATTRIBUTE_NAME} from '../../validations/attribute-name-validation';
import {ColumnWidth} from '../constants';

import {DataTypeDropdown} from './data-type/data-type-dropdown';
import {PossibleValuesModal} from './possible-value-modal/possible-values-modal';
import {attributeMapToAttributeRowInfo} from './utils/attributeMapToAttributeRowInfo';

type Props = {
    mode?: EventCreationMode,
    validationHighLight: boolean,
    isDeleteDisabled?: boolean,
    template?: CreationAttribute,
    attribute?: CreationAttribute,
    addedAttribute?: CreationAttribute,
    attributeUsages?: Array<AttributeUsage>,
    changeRequestId: number | null,
    eventChangeRequest?: EventChangeRequest,
    isEventMarkedDeprecated?: boolean,
    onRemove?: (removeAttribute: CreationAttribute) => void,
    onChangeAttribute?: (changedAttribute: CreationAttribute) => void,
};

type ValidInputsValues = {
    attributeName: string,
    description: string,
};

const PLACEHOLDER = {
    attribute: 'Attribute Name',
    dataType: 'Data Type',
    possibleValues: 'Possible Values',
};

const REQUIRED_TOOLTIP_CAPTION = 'The new attribute cannot be required';
const AUTO_HIDE_TIMEOUT = 10000;
const NO_ATTRIBUTE_USAGE = 'No Usage';

export const AttributeFormRow = (props: Props) => {
    const {
        mode,
        attribute,
        addedAttribute,
        template,
        attributeUsages,
        changeRequestId,
        eventChangeRequest,
        onChangeAttribute,
        validationHighLight,
        isDeleteDisabled,
        isEventMarkedDeprecated,
        onRemove,
    } = props;

    const currentAttribute = useMemo(() => {
        return addedAttribute || attributeMapToAttributeRowInfo(attribute)
            || attributeMapToAttributeRowInfo(template);
    }, [addedAttribute, attribute, template]);
    const [showValidation, setShowValidation] = useState(false);
    const [lastUsageDate, setLastUsageDate] = useState<string>('');
    const {onCreateNote} = usePushNotification();
    const [validInputsValues, setValidInputsValues] = useState<ValidInputsValues>({
        attributeName: currentAttribute?.name ?? '',
        description: currentAttribute?.description ?? '',
    });

    const possibleValues = useMemo(() => currentAttribute?.possibleValues ?? [],
        [currentAttribute?.possibleValues]);

    const possibleValuesDisplay = possibleValues.length
        ? possibleValuesToString(possibleValues)
        : NO_DATA_AVAILABLE;

    const isReadOnly = template || lastUsageDate;

    const onCheckBoxRequired = (data: {event: React.SyntheticEvent<HTMLInputElement>, name: string}) => {
        if (!currentAttribute || !onChangeAttribute) {
            return;
        }

        onChangeAttribute({...currentAttribute, [FieldName.REQUIRED]: data.event.currentTarget.checked});
    };

    const onCheckBoxDeprecated = (data: {event: React.SyntheticEvent<HTMLInputElement>, name: string}) => {
        if (!currentAttribute || !onChangeAttribute) {
            return;
        }

        onChangeAttribute({...currentAttribute, [FieldName.DEPRECATED]: data.event.currentTarget.checked});
    };

    const onChangeField = (value: string, name?: string, itemsType?: string) => {
        if (!currentAttribute || !onChangeAttribute || !name) {
            return;
        }

        if (name === FieldName.FIELD_TYPE) {
            const currentAttributeResolveItemsType =
                !itemsType ? {...omit(currentAttribute, 'itemsType'), [name]: value} :
                        {...currentAttribute, [name]: value, itemsType};

            onChangeAttribute(currentAttributeResolveItemsType);
            return;
        }

        onChangeAttribute({...currentAttribute, [name]: value});
    };

    const onChangePossibleValues = (values: string[]) => {
        if (!currentAttribute || !onChangeAttribute) {
            return;
        }

        onChangeAttribute({...currentAttribute, [FieldName.POSSIBLE_VALUES]: values});
    };

    const onDescriptionSuggestSelect = (suggest: string) => {
        setValidInputsValues({...validInputsValues, description: suggest});
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const {name, value} = event.target;
        if (name && name === FieldName.NAME) {
            onChangeField(sanitizeSnakeCase(value).toLowerCase(), name);
            setValidInputsValues({...validInputsValues, attributeName: sanitizeSnakeCase(value).toLowerCase()});
            return;
        }

        if (name && name === FieldName.DESCRIPTION) {
            onChangeField(value.trim(), name);
            setValidInputsValues({...validInputsValues, description: value.trim()});
            return;
        }
        onChangeField(value, name);
    };

    const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = event.target;

        if (name && name === FieldName.NAME) {
            setValidInputsValues({...validInputsValues, attributeName: value});
        }

        if (name && name === FieldName.DESCRIPTION) {
            setValidInputsValues({...validInputsValues, description: value});
        }
    }, [validInputsValues]);

    useEffect(() => {
        if (currentAttribute?.id && attributeUsages) {
            const attributeUsage =
                attributeUsages.find(usage => usage.id === Number(currentAttribute.id));

            if (attributeUsage) {
                setLastUsageDate(attributeUsage.lastUsageDate);
            }
        }
    }, [attributeUsages, currentAttribute]);

    useEffect(() => {
        if (addedAttribute && isAttributeEmpty(addedAttribute)) {
            setShowValidation(false);
            return;
        }

        setShowValidation(validationHighLight);
    }, [addedAttribute, validationHighLight]);

    useEffect(() => {
        if (validInputsValues.attributeName.match(FIELDS_PATTERN.attributeName)) {
            onCreateNote({
                id: 'attribute-name-warning',
                status: Status.WARNING,
                message: 'Only Latin characters and _ symbol are allowed.',
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: AUTO_HIDE_TIMEOUT,
            });

            setValidInputsValues({
                ...validInputsValues,
                attributeName: validInputsValues.attributeName.replace(FIELDS_PATTERN.attributeName, ''),
            });
        }

        if (validInputsValues.description.match(FIELDS_PATTERN.description)) {
            onCreateNote({
                id: 'attribute-description-warning',
                status: Status.WARNING,
                message: FIELDS_PATTERN_VALIDATION_MESSAGE,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: AUTO_HIDE_TIMEOUT,
            });

            setValidInputsValues({
                ...validInputsValues,
                description: validInputsValues.description.replace(FIELDS_PATTERN.description, ''),
            });
        }
    }, [onCreateNote, onChange, validInputsValues]);

    const onRemoveHandle = () => {
        if (!onRemove || !currentAttribute) {
            return;
        }

        onRemove(currentAttribute);
    };

    const isAttributeNewForExistingEvent = Boolean(mode === EventCreationMode.update && addedAttribute);
    const usageDate = lastUsageDate ? formatDate(lastUsageDate) : NO_ATTRIBUTE_USAGE;

    const requiredCheckBox = (
        <CheckBox
            name={FieldName.REQUIRED}
            checked={currentAttribute?.required ?? false}
            onCheckBoxChange={onCheckBoxRequired}
            disabled={!!template || REQUIRED_ATTRIBUTES.includes(currentAttribute?.name || '') ||
                isAttributeNewForExistingEvent}
        />
    );

    return (
        <Item style={{width: '100%'}}>
            <Group gap="s" alignItems="center">
                <Item style={{width: ColumnWidth.attribute}}>
                    <Input
                        name={FieldName.NAME}
                        maxLength={MAX_LENGTH_ATTRIBUTE_NAME}
                        className="ellipsis"
                        color={
                            showValidation &&
                            (validInputsValues.attributeName &&
                                validInputsValues.attributeName.length < MIN_LENGTH_ATTRIBUTE_NAME ||
                                !validInputsValues.attributeName) ? 'ERROR' : 'DEFAULT'
                        }
                        placeholder={PLACEHOLDER.attribute}
                        value={validInputsValues.attributeName ?? ''}
                        disabled={!!attribute || !!template}
                        autoComplete="off"
                        onBlur={onBlur}
                        onChange={onChange}
                        endAdornment={(
                            <AttributeReviewFeedback
                                changeRequestId={changeRequestId}
                                attributeName={validInputsValues.attributeName}
                            />
                        )}
                    />
                </Item>
                <Item style={{width: ColumnWidth.description}}>
                    <span title={validInputsValues.description ?? ''}>
                        <DescriptionAutosuggest
                            attributeName={currentAttribute?.name}
                            attributeType={currentAttribute?.fieldType}
                            eventChangeRequest={eventChangeRequest}
                            onSuggestSelect={onDescriptionSuggestSelect}
                            disabled={!!template}
                            hasError={
                                Boolean(showValidation &&
                                    (validInputsValues.description &&
                                        attributeDescriptionValidation(validInputsValues.description.trim()).length ||
                                        !validInputsValues.description))
                            }
                            onBlur={onBlur}
                            value={validInputsValues.description ?? ''}
                            onChangeField={onChangeField}
                            onChange={onChange}
                        />
                    </span>
                </Item>
                <Item style={{width: ColumnWidth.dataType}}>
                    {!attribute && !template || !!attribute && !!template ? (
                        <DataTypeDropdown
                            fieldType={currentAttribute?.fieldType}
                            itemsType={currentAttribute?.itemsType}
                            placeholder={PLACEHOLDER.dataType}
                            validationHighLight={showValidation}
                            onChangeDataType={onChangeField}
                        />
                    ) : (
                        <Input
                            name={FieldName.FIELD_TYPE}
                            placeholder={PLACEHOLDER.dataType}
                            className="ellipsis"
                            value={dataTypeString(
                                currentAttribute?.fieldType as FieldType,
                                currentAttribute?.itemsType as ItemsType,
                            )}
                            disabled={!attribute && !!template || !!attribute}
                        />
                    )}
                </Item>
                <Item style={{width: ColumnWidth.possibleValues}}>
                    {isReadOnly ? (
                        <Input
                            name={FieldName.POSSIBLE_VALUES}
                            placeholder={PLACEHOLDER.possibleValues}
                            className="ellipsis"
                            value={possibleValuesDisplay}
                            disabled={true}
                        />
                    ) : (
                        <PossibleValuesModal
                            attributeName={currentAttribute?.name}
                            possibleValues={possibleValues}
                            placeholder={attribute ? possibleValuesDisplay : PLACEHOLDER.possibleValues}
                            validationHighLight={showValidation}
                            onChangePossibleValues={onChangePossibleValues}
                        />
                    )}
                </Item>
                {mode === EventCreationMode.update && (
                    <Item style={{width: ColumnWidth.lastUsageDate}}>
                        <Input
                            style={{textAlign: 'center'}}
                            className="ellipsis"
                            name={FieldName.ATTRIBUTE_LAST_USAGE_DATE}
                            value={currentAttribute?.id ? usageDate : ''}
                            disabled={true}
                        />
                    </Item>
                )}
                <Item style={{width: ColumnWidth.required}}>
                    <Group justify="center">
                        {isAttributeNewForExistingEvent
                            ? (
                                    <Tooltip
                                        anchor={({anchorRef}) => (
                                            <span ref={anchorRef}>{requiredCheckBox}</span>
                                        )}>
                                        {REQUIRED_TOOLTIP_CAPTION}
                                    </Tooltip>
                                )
                            : requiredCheckBox}
                    </Group>
                </Item>
                {mode === EventCreationMode.update && (
                    <Item style={{width: ColumnWidth.deprecated}}>
                        <Group justify="center">
                            <CheckBox
                                name={FieldName.DEPRECATED}
                                checked={currentAttribute?.deprecated ?? false}
                                onCheckBoxChange={onCheckBoxDeprecated}
                                disabled={!!template || !!addedAttribute || isEventMarkedDeprecated
                                    || REQUIRED_ATTRIBUTES.includes(currentAttribute?.name || '')}
                            />
                        </Group>
                    </Item>
                )}
                <Item style={{width: ColumnWidth.button}}>
                    {addedAttribute ? (
                        <Tooltip
                            anchor={({anchorRef}) => (
                                <BasicIconButton
                                    disabled={isDeleteDisabled}
                                    ref={anchorRef}
                                    iconProps={{type: Icons.TRASH_LIGHT, size: Sizes.Size_19}}
                                    onClick={onRemoveHandle}
                                />
                            )}>
                            Delete Attribute
                        </Tooltip>
                    ) : null}
                </Item>
            </Group>
        </Item>
    );
};
